import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { appUrl } from '@astro/config'

// Same display semantics as the shop storefront (apps/shop pujaFormat.js):
// festival_name wins over the schedule_type label; prices are starting_price.
const SCHEDULE_LABELS = {
  one_time: 'One-Time Vidhi',
  daily: 'Daily Seva',
  weekly: 'Weekly Seva',
  monthly: 'Monthly Seva',
  yearly: 'Yearly Vidhan',
  festival: 'Festival Special',
}
const scheduleLabel = (p) => p?.festival_name || SCHEDULE_LABELS[p?.schedule_type] || 'Vedic Ritual'
const formatRupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`

const FLAME_PATH =
  'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z'

function PoojaCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/4 p-5 backdrop-blur-sm animate-pulse">
      <div className="flex items-center justify-between">
        <span className="h-14 w-14 rounded-xl bg-white/10" />
        <span className="h-7 w-20 rounded-full bg-white/10" />
      </div>
      <span className="mt-4 h-4 w-3/4 rounded bg-white/10" />
      <span className="mt-2 h-3 w-full rounded bg-white/5" />
      <span className="mt-1.5 h-3 w-2/3 rounded bg-white/5" />
      <span className="mt-3 h-3 w-1/2 rounded bg-white/5" />
      <span className="mt-4 h-10 rounded-xl bg-white/10" />
    </div>
  )
}

export default function PoojaSection() {
  // Home-page teaser rides the live puja catalog: admin-curated featured
  // pujas first, else the first rows of GET /pujas — never static data.
  const { data: pujas = [], isPending } = useQuery({
    queryKey: ['pujas', 'home-teaser'],
    queryFn: async () => {
      const featured = await api.puja.featured(4)
      if ((featured?.data ?? []).length > 0) return featured.data
      const all = await api.puja.list({ limit: 4 })
      return all?.data ?? []
    },
    staleTime: 10 * 60 * 1000,
  })

  return (
    <section className="relative overflow-hidden hero-vedic py-16 md:py-24 md:px-18 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[radial-gradient(#E6C878_1px,transparent_1.5px)] bg-size-[26px_26px]" />
      <div className="container relative z-10 mx-auto px-6 max-w-350">
        <div className="mx-auto mb-10 flex max-w-2xl flex-col items-center text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-secondary/15 text-secondary ring-1 ring-secondary/25">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d={FLAME_PATH} />
            </svg>
          </span>
          <div className="mt-4 flex items-center gap-3">
            <span className="h-px w-8 bg-secondary/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-secondary">Sacred Rituals</span>
            <span className="h-px w-8 bg-secondary/50" />
          </div>
          <h2 className="mt-3 font-cinzel text-3xl uppercase leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
            Pooja <span className="text-gradient-gold">Energization</span>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-base normal-case">
            Authentic Vedic poojas performed and energised by Astrologer Raju Maharaj Ji — with full vidhi, and the blessed prasad couriered to your home.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-secondary/25 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-widest text-secondary">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Certified Pandits
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-secondary/25 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-widest text-secondary">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
              </svg>
              Energised &amp; Blessed
            </span>
          </div>
        </div>

        {(isPending || pujas.length > 0) && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {isPending
              ? Array.from({ length: 4 }, (_, i) => <PoojaCardSkeleton key={i} />)
              : pujas.map((pooja) => (
                  <div
                    key={pooja.id}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/4 p-5 backdrop-blur-sm transition-all duration-400 hover:-translate-y-1.5 hover:border-secondary/45 hover:bg-white/6"
                  >
                    <span className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-secondary/0 via-secondary/70 to-secondary/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <div className="flex items-center justify-between">
                      <span className="relative grid h-14 w-14 place-items-center">
                        <svg viewBox="0 0 56 56" className="absolute inset-0 h-full w-full animate-[spin_16s_linear_infinite] motion-reduce:animate-none" fill="none">
                          <circle cx="28" cy="28" r="26" stroke="#E6C878" strokeOpacity="0.22" strokeWidth="1" strokeDasharray="3 6" />
                        </svg>
                        <span className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/12 text-secondary ring-1 ring-secondary/25 transition-all duration-400 group-hover:bg-secondary group-hover:text-primary">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                            <path d={FLAME_PATH} />
                          </svg>
                        </span>
                      </span>
                      {pooja.starting_price > 0 && (
                        <span className="rounded-full bg-secondary/15 px-3 py-1 font-cinzel text-sm text-secondary">{formatRupees(pooja.starting_price)}</span>
                      )}
                    </div>
                    <h3 className="mt-4 font-cinzel text-base leading-tight text-[#FBF3E4] transition-colors duration-400 group-hover:text-secondary">{pooja.title}</h3>
                    <p className="mt-1.5 text-[12px] leading-relaxed text-white/55 line-clamp-2">{pooja.short_description || pooja.subtitle}</p>
                    <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5 shrink-0 text-secondary">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span className="line-clamp-1">
                        {scheduleLabel(pooja)}
                        {pooja.temple?.name ? ` · ${pooja.temple.name}` : ''}
                      </span>
                    </div>
                    {/* The gap above the CTA lives on this wrapper: `pt-4` on the
                        anchor itself collided with its own `py-2.5`, inflating
                        the padding inside the button's border. */}
                    <div className="mt-auto pt-4">
                      <a
                        href={appUrl('shop', `pooja-energization/${pooja.slug}`)}
                        className="flex h-10 w-full items-center justify-center gap-1.5 rounded-xl border border-secondary/30 bg-secondary/10 px-3 text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap text-secondary transition-all duration-400 group-hover:bg-secondary group-hover:text-primary"
                      >
                        Book This Pooja
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5 shrink-0 transition-transform duration-400 group-hover:translate-x-1">
                          <path d="M5 12h14" />
                          <path d="m12 5 7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  </div>
                ))}
          </div>
        )}

        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={appUrl("shop", "pooja-energization")}
            className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-95 py-2 group h-12 rounded-full bg-secondary px-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary shadow-lg shadow-secondary/20 transition-all hover:bg-[#FBF3E4]"
          >
            View All Poojas
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
          <Link
            to="/maharaj-ji"
            className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-95 border py-2 h-12 rounded-full border-secondary/40 bg-transparent px-4 md:px-8 text-[9px] md:text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FBF3E4] transition-all hover:border-secondary hover:bg-secondary/15 hover:text-[#FBF3E4]"
          >
            Not Sure Which Pooja? Consult Maharaj Ji
          </Link>
        </div>
      </div>
    </section>
  )
}
